import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { fetchWithToken } from "helpers/fetch";
import { endPoints } from "const/endPoints";
import Spinner from "components/common/Spinner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import GetAppIcon from "@mui/icons-material/GetApp";
import AssessmentIcon from "@mui/icons-material/Assessment";

const UserProgress = () => {
  const [usersProgress, setUsersProgress] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    averageProgress: 0,
    passedUsers: 0,
    failedUsers: 0,
    insufficientUsers: 0,
  });

  // Las notas vienen del API ya en escala 0-20; aquí solo se formatean.
  const formatGrade = (score) => Number(score || 0).toFixed(2);

  // Función para obtener el promedio de scores de un usuario (escala 0-20)
  const calculateUserAverage = (userScores) => {
    if (!userScores || userScores.length === 0) return 0;
    const total = userScores.reduce((sum, score) => sum + score.score, 0);
    return total / userScores.length;
  };

  // Función para obtener progreso de usuarios
  const fetchUserProgress = async () => {
    try {
      setLoading(true);

      // Usar el nuevo endpoint que trae todo el progreso de usuarios
      const progressResponse = await fetchWithToken(
        endPoints.get_all_users_progress
      );

      if (progressResponse.status !== 200) {
        console.error("Error fetching user progress");
        return;
      }

      const progressData = await progressResponse.json();
      console.log("Raw progress data:", progressData);

      // Transformar los datos para el formato del frontend
      const transformedData = progressData.map((userProgress) => {
        const user = userProgress.user;
        const averageScore = userProgress.averageScore || 0;

        return {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          totalQuizzes: userProgress.totalQuizzes,
          averageScore: formatGrade(averageScore),
          status: userProgress.status,
          scores: userProgress.scores,
        };
      });

      // También incluir usuarios sin scores que no aparecen en la respuesta
      const usersResponse = await fetchWithToken(endPoints.get_all_users);
      if (usersResponse.status === 200) {
        const allUsers = await usersResponse.json();
        const usersWithProgress = transformedData.map((p) => p.id);

        // Agregar usuarios que no tienen scores
        const usersWithoutProgress = allUsers
          .filter((user) => !usersWithProgress.includes(user.id))
          .map((user) => ({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role,
            totalQuizzes: 0,
            averageScore: "0.00",
            status: "SIN DATOS",
            scores: [],
          }));

        const allProgressData = [...transformedData, ...usersWithoutProgress];
        setUsersProgress(allProgressData);
        calculateStats(allProgressData);
      } else {
        setUsersProgress(transformedData);
        calculateStats(transformedData);
      }
    } catch (error) {
      console.error("Error fetching user progress:", error);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener cursos
  const fetchCourses = async () => {
    try {
      const coursesResponse = await fetchWithToken(endPoints.get_all_courses);
      if (coursesResponse.status === 200) {
        const coursesData = await coursesResponse.json();
        setCourses(coursesData);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  // Calcular estadísticas
  const calculateStats = (data) => {
    const totalUsers = data.length;
    const studentsOnly = data.filter((user) => user.role === "estudiante");

    if (studentsOnly.length === 0) {
      setStats({
        totalUsers: totalUsers,
        averageProgress: 0,
        passedUsers: 0,
        failedUsers: 0,
        insufficientUsers: 0,
      });
      return;
    }

    const totalProgress = studentsOnly.reduce(
      (sum, user) => sum + parseFloat(user.averageScore),
      0
    );
    const averageProgress = totalProgress / studentsOnly.length;
    const passedUsers = studentsOnly.filter(
      (user) => user.status === "APROBADO"
    ).length;
    const failedUsers = studentsOnly.filter(
      (user) => user.status === "REPROBADO"
    ).length;
    const insufficientUsers = studentsOnly.filter(
      (user) => user.status === "PROGRESO INSUFICIENTE"
    ).length;

    setStats({
      totalUsers: totalUsers,
      averageProgress: averageProgress.toFixed(2),
      passedUsers,
      failedUsers,
      insufficientUsers,
    });
  };

  // Filtrar datos por curso si está seleccionado
  const filteredData = usersProgress.filter((user) => {
    if (user.role !== "estudiante") return false;

    if (selectedCourse === "all") return true;

    // Filtrar por curso específico si tiene scores
    return (
      user.scores &&
      user.scores.some((score) => score.course?.id === selectedCourse)
    );
  });

  // Calcular estadísticas para datos filtrados
  const getFilteredStats = () => {
    const studentsOnly = filteredData.filter(
      (user) => user.role === "estudiante"
    );

    if (studentsOnly.length === 0) {
      return {
        totalUsers: 0,
        averageProgress: 0,
        passedUsers: 0,
        failedUsers: 0,
        insufficientUsers: 0,
      };
    }

    const totalProgress = studentsOnly.reduce(
      (sum, user) => sum + parseFloat(user.averageScore),
      0
    );
    const averageProgress = totalProgress / studentsOnly.length;
    const passedUsers = studentsOnly.filter(
      (user) => user.status === "APROBADO"
    ).length;
    const failedUsers = studentsOnly.filter(
      (user) => user.status === "REPROBADO"
    ).length;
    const insufficientUsers = studentsOnly.filter(
      (user) => user.status === "PROGRESO INSUFICIENTE"
    ).length;

    return {
      totalUsers: studentsOnly.length,
      averageProgress: averageProgress.toFixed(2),
      passedUsers,
      failedUsers,
      insufficientUsers,
    };
  };

  const filteredStats = getFilteredStats();

  // Función para debuggear datos de usuarios (temporal)
  const debugUserData = (user) => {
    console.log(`=== DEBUG USER: ${user.firstName} ${user.lastName} ===`);
    console.log("BASIC DATA:", {
      ci: user.ci,
      firstName: user.firstName,
      lastName: user.lastName,
      averageScore: user.averageScore,
      totalQuizzes: user.totalQuizzes,
    });

    console.log("QUIZ DETAILS:", user.quizDetails);
    console.log("SCORES ARRAY:", user.scores);

    // Debug detallado de quizDetails
    if (user.quizDetails) {
      console.log("📋 QuizDetails breakdown:");
      Object.entries(user.quizDetails).forEach(([key, details], index) => {
        console.log(`  ${index + 1}. Key: "${key}"`);
        console.log(`     Name: "${details.quizName || "NO NAME"}"`);
        console.log(`     Score: ${details.score || "NO SCORE"}`);
        console.log(`     Course: "${details.courseName || "NO COURSE"}"`);
      });
    } else {
      console.log("❌ NO quizDetails found");
    }

    // Debug detallado de scores array
    if (user.scores && user.scores.length > 0) {
      console.log("📊 Scores array breakdown:");
      user.scores.forEach((score, index) => {
        console.log(`  ${index + 1}. Raw score object:`, score);
        if (score.quiz) {
          console.log(`     Quiz.name: "${score.quiz.name || "NO NAME"}"`);
          console.log(`     Quiz.id: ${score.quiz.id || "NO ID"}`);
        }
        if (score.course) {
          console.log(`     Course.name: "${score.course.name || "NO NAME"}"`);
          console.log(`     Course.id: ${score.course.id || "NO ID"}`);
        }
        console.log(`     Score value: ${score.score || "NO SCORE"}`);
      });
    } else {
      console.log("❌ NO scores array found");
    }

    console.log("=== END DEBUG ===");
  };

  // Función para exportar a Excel en formato universitario
  const exportToExcel = () => {
    const dataToExport =
      selectedCourse === "all"
        ? usersProgress.filter((user) => user.role === "estudiante")
        : filteredData;

    // Debug para los primeros usuarios con datos
    console.log("=== DEBUGGING EXCEL EXPORT ===");
    const usersWithData = dataToExport.filter((user) => user.totalQuizzes > 0);
    console.log(`Found ${usersWithData.length} users with quiz data`);

    // Debug los primeros 3 usuarios con datos
    usersWithData.slice(0, 3).forEach((user, index) => {
      console.log(`--- User ${index + 1} ---`);
      debugUserData(user);
    });

    const workbook = XLSX.utils.book_new();

    // Crear la estructura universitaria
    const universityData = [];

    // Las columnas de quizzes se derivan de los quizzes que existen de verdad.
    // Antes estaban fijas en 8 cuando el curso tiene 7, asi que salia una
    // columna fantasma en 0 para todos y los encabezados quedaban corridos
    // una posicion respecto a los datos.
    const quizNames = Array.from(
      new Set(
        dataToExport
          .flatMap((user) => user.scores || [])
          .map((score) => score?.quiz?.name)
          .filter(Boolean)
      )
    ).sort((a, b) =>
      a.localeCompare(b, "es", { numeric: true, sensitivity: "base" })
    );

    const quizCount = quizNames.length;
    const COL_PRACTICA = 3 + quizCount;
    const COL_TRABAJO = COL_PRACTICA + 1;
    const COL_TOTAL = COL_TRABAJO + 1;
    const COL_PROMEDIO = COL_TOTAL + 1;
    const TOTAL_COLS = COL_PROMEDIO + 1;

    // Construye una fila del ancho exacto de la hoja a partir de pares
    // [columna, valor], de modo que todas las filas queden alineadas.
    const buildRow = (entries) => {
      const row = new Array(TOTAL_COLS).fill("");
      entries.forEach(([col, value]) => {
        row[col] = value;
      });
      return row;
    };

    // Header principal
    universityData.push(
      buildRow([
        [0, "MODULO"],
        [COL_PRACTICA, "Practica Presencial"],
        [COL_TRABAJO, "Trabajo Final"],
        [COL_TOTAL, "Total"],
        [COL_PROMEDIO, "Promedio (0-20)"],
      ])
    );

    // Subheader de Quizzes
    universityData.push(
      buildRow(quizNames.map((name, i) => [3 + i, name]))
    );

    // Fila de ponderaciones: cada quiz pesa 1; practica y trabajo final 0.
    universityData.push(
      buildRow([
        [0, "Ponderación"],
        ...quizNames.map((_, i) => [3 + i, "1"]),
        [COL_PRACTICA, "0"],
        [COL_TRABAJO, "0"],
      ])
    );

    // Header de columnas principales
    universityData.push(
      buildRow([
        [0, "N°"],
        [1, "Nombres y Apellidos"],
        [2, "Cédula de Identidad"],
        ...quizNames.map((_, i) => [3 + i, "CALF"]),
        [COL_PRACTICA, "CALF"],
        [COL_TRABAJO, "CALF"],
        [COL_TOTAL, "CALF"],
        [COL_PROMEDIO, "CALF"],
      ])
    );

    // Agregar estudiantes
    dataToExport.forEach((user, index) => {
      const entries = [
        [0, index + 1],
        [1, `${user.firstName} ${user.lastName}`],
        [2, user.ci || ""],
      ];

      // Nota de cada quiz, en escala 0-20
      // Un quiz no presentado se deja en blanco para poder distinguirlo de un
      // 0 realmente obtenido; para el Total cuenta como 0, igual que antes.
      let sumaQuizzes = 0;
      quizNames.forEach((name, i) => {
        const nota = getQuizScoreByName(user, name);
        if (nota === null) {
          entries.push([3 + i, ""]);
          return;
        }
        sumaQuizzes += nota;
        entries.push([3 + i, formatExcelGrade(nota)]);
      });

      // Practica Presencial y Trabajo Final: aun no se registran
      entries.push([COL_PRACTICA, "0,0"]);
      entries.push([COL_TRABAJO, "0,0"]);

      // Total: promedio sobre los quizzes realmente existentes
      const total = quizCount > 0 ? sumaQuizzes / quizCount : 0;
      entries.push([COL_TOTAL, formatExcelGrade(total)]);

      // Promedio que muestra el panel (solo quizzes presentados)
      entries.push([COL_PROMEDIO, formatExcelGrade(parseFloat(user.averageScore))]);

      universityData.push(buildRow(entries));
    });

    // Crear worksheet con los datos
    const worksheet = XLSX.utils.aoa_to_sheet(universityData);

    // Aplicar estilos y merge de celdas si es necesario
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    // Merge header cells
    if (!worksheet["!merges"]) worksheet["!merges"] = [];

    // Merges calculados sobre las columnas reales (antes estaban fijos en 11-14
    // y no coincidian con los datos).
    // "MODULO" abarca desde la primera columna hasta el ultimo quiz.
    worksheet["!merges"].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: COL_PRACTICA - 1 },
    });

    // Cabeceras de la derecha: merge vertical sobre las dos primeras filas.
    [COL_PRACTICA, COL_TRABAJO, COL_TOTAL, COL_PROMEDIO].forEach((col) => {
      worksheet["!merges"].push({
        s: { r: 0, c: col },
        e: { r: 1, c: col },
      });
    });

    XLSX.utils.book_append_sheet(workbook, worksheet, "Calificaciones");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const courseFileName =
      selectedCourse === "all"
        ? "todos_cursos"
        : courses
            .find((c) => c.id === selectedCourse)
            ?.title.toLowerCase()
            .replace(/\s+/g, "_") || "curso";
    const fileName = `calificaciones_${courseFileName}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    saveAs(data, fileName);
  };

  // Nota FINAL de un quiz: la del ULTIMO intento (el 2do si lo hizo), sea
  // mejor o peor que el primero. Se ordena por attemptNumber y, si empatan,
  // por fecha. Antes se usaba .find(), que devolvia un intento arbitrario.
  const getQuizScoreByName = (user, quizName) => {
    if (!user.scores || user.scores.length === 0) return null;

    const intentos = user.scores
      .filter(
        (score) =>
          score?.quiz?.name === quizName && !Number.isNaN(Number(score.score))
      )
      .sort((a, b) => {
        const porIntento = (a.attemptNumber || 0) - (b.attemptNumber || 0);
        if (porIntento !== 0) return porIntento;
        return new Date(a.attemptDate || 0) - new Date(b.attemptDate || 0);
      });

    if (intentos.length === 0) return null;
    return Number(intentos[intentos.length - 1].score);
  };

  // Excel en es-VE usa la coma como separador decimal.
  const formatExcelGrade = (value) => {
    const n = Number(value);
    return (Number.isNaN(n) ? 0 : n).toFixed(2).replace(".", ",");
  };

  // Template para mostrar el estado
  const statusTemplate = (rowData) => {
    const getColor = (status) => {
      switch (status) {
        case "APROBADO":
          return "success";
        case "REPROBADO":
          return "error";
        case "PROGRESO INSUFICIENTE":
          return "warning";
        default:
          return "default";
      }
    };

    const getLabel = (status) => {
      switch (status) {
        case "PROGRESO INSUFICIENTE":
          return "INSUFICIENTE";
        default:
          return status;
      }
    };

    return (
      <Chip
        label={getLabel(rowData.status)}
        color={getColor(rowData.status)}
        variant="filled"
        size="small"
      />
    );
  };

  useEffect(() => {
    fetchUserProgress();
    fetchCourses();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{ mb: 3, display: "flex", alignItems: "center" }}
      >
        <AssessmentIcon sx={{ mr: 1 }} />
        Progreso de Estudiantes
      </Typography>

      {/* Estadísticas */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {selectedCourse === "all"
                ? "Total Estudiantes"
                : "Estudiantes en Curso"}
            </Typography>
            <Typography variant="h4">{filteredStats.totalUsers}</Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Promedio (0-20)
            </Typography>
            <Typography variant="h4" color="primary">
              {filteredStats.averageProgress}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Aprobados
            </Typography>
            <Typography variant="h4" color="success.main">
              {filteredStats.passedUsers}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Reprobados
            </Typography>
            <Typography variant="h4" color="error.main">
              {filteredStats.failedUsers}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Progreso Insuficiente
            </Typography>
            <Typography variant="h4" color="warning.main">
              {filteredStats.insufficientUsers}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Mostrar información del curso seleccionado */}
      {selectedCourse !== "all" && (
        <Card sx={{ mb: 3, bgcolor: "info.light" }}>
          <CardContent>
            <Typography variant="h6" color="info.contrastText">
              📊 Filtro Activo:{" "}
              {courses.find((c) => c.id === selectedCourse)?.title || "Curso"}
            </Typography>
            <Typography variant="body2" color="info.contrastText">
              Mostrando solo estudiantes que han realizado quizzes en este curso
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Controles */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filtrar por Curso</InputLabel>
          <Select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            label="Filtrar por Curso"
          >
            <MenuItem value="all">Todos los Cursos</MenuItem>
            {courses.map((course) => (
              <MenuItem key={course.id} value={course.id}>
                {course.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={exportToExcel}
          sx={{ height: "fit-content" }}
        >
          Exportar a Excel
        </Button>
      </Box>

      {/* Tabla de Datos */}
      <Card>
        <CardContent>
          <DataTable
            value={filteredData}
            paginator
            rows={10}
            dataKey="id"
            loading={loading}
            emptyMessage="No hay datos de estudiantes disponibles"
            responsiveLayout="scroll"
          >
            <Column field="firstName" header="Nombre" sortable />
            <Column field="lastName" header="Apellido" sortable />
            <Column field="email" header="Email" sortable />
            <Column field="totalQuizzes" header="Quizzes" sortable />
            <Column
              field="averageScore"
              header="Promedio (0-20)"
              sortable
              body={(rowData) => rowData.averageScore}
            />
            <Column
              field="status"
              header="Estado"
              body={statusTemplate}
              sortable
            />
          </DataTable>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserProgress;
